// …/payment-table.tsx
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import Table from "@core/components/table";
import { paymentColumns } from "./payment-columns";
import WidgetCard from "@core/components/cards/widget-card";
import { useTanStackTable } from "@core/components/table/custom/use-TanStack-Table";
import TablePagination from "@core/components/table/pagination";
import cn from "@core/utils/class-names";
import Filters from "./payments-filters";
import type { Payments } from "packages/app/src/types";
import { deletePayment } from "../../../../api/delete-payment";

interface PaymentsProps {
  payments: Payments[];
  isLoading: boolean;
  className?: string;
  error: Error | null;
}

export default function PaymentTable({
  className,
  payments,
  isLoading,
  error,
}: PaymentsProps) {
  const { table, setData } = useTanStackTable<Payments>({
    tableData: payments,
    columnConfig: paymentColumns,
    options: {
      initialState: { pagination: { pageIndex: 0, pageSize: 5 } },
      meta: {
        handleDeleteRow: async (row) => {
          try {
            await deletePayment(row._id);
            setData((prev) => prev.filter((r) => r._id !== row._id));
          } catch (error) {
            console.log("Error deleting payment, ", error);
          }
        },
      },
      enableColumnResizing: false,
    },
  });

  useEffect(() => {
    setData(payments);
  }, [payments, setData]);

  useEffect(() => {
    table.setColumnOrder([
      "actions",
      "_id",
      "campaignId",
      "status",
      "amount",
      "totalAmount",
      "tip",
      "donor",
      "currencyType",
      "createdAt",
    ]);
  }, [table]);

  if (isLoading)
    return (
      <div className="col-span-full h-60 flex justify-center items-center text-[16px] font-semibold">
        Loading payments…
      </div>
    );
  if (error) return <div className="text-red-600">Error: {error.message}</div>;

  return (
    <WidgetCard
      className={cn("p-0 lg:p-0", className)}
      title="Payments"
      titleClassName="w-[19ch]"
      actionClassName="w-full ps-0 items-center"
      headerClassName="mb-6 items-start flex-col @[57rem]:flex-row @[57rem]:items-center px-5 pt-5 lg:pt-7 lg:px-7"
      action={<Filters table={table} />}
    >
      <Table table={table} variant="modern" />
      <TablePagination table={table} className="p-4" />
    </WidgetCard>
  );
}
